import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, FileCheck, ShieldCheck, Radio, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Eyebrow, ChapterHeading } from "../components/Reveal";
import { SITE, REGION_ORDER, locationsByRegion } from "../data/site";

const STEPS = [
  {
    icon: Radio,
    title: "Call or email command",
    text: "Speak directly to our 24/7 England command centre — no call queues, no callbacks. Tell us what you need protecting and where.",
  },
  {
    icon: ShieldCheck,
    title: "Free risk assessment",
    text: "We survey your site, map the local risk picture and recommend only what you need — in writing, without obligation.",
  },
  {
    icon: FileCheck,
    title: "Written quote & deployment",
    text: "Same-day written quote with duties, checkpoints and KPIs. Urgent deployments are typically on site within 24 hours.",
  },
];

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact Our 24/7 Security Command Centre | Xtreme Protection England"
        description={`Contact Xtreme Protection for security services anywhere in England. Call ${SITE.phone} (24/7) or email ${SITE.email} — free risk assessment and same-day written quotes.`}
        keywords="contact security company England, security quote, hire security guards, 24 hour security phone number, SIA security company contact"
        canonical={`${SITE.domain}/contact`}
      />

      <section className="tactical-grid border-b border-white/10" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <Reveal>
            <Eyebrow>Speak to Command // 24/7/365</Eyebrow>
            <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl text-white max-w-4xl">
              Contact Xtreme Protection
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-gray-300 leading-relaxed">
              One call reaches a real decision-maker, day or night. Free risk
              assessment, same-day written quotes, and urgent deployments
              typically on site within 24 hours — anywhere in England.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20" data-testid="contact-channels">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Reveal>
            <a
              href={SITE.phoneHref}
              data-testid="contact-phone-card"
              className="corner-card spotlight group block bg-card border border-white/10 p-8 hover:border-amber/60 transition-colors duration-300 h-full"
            >
              <Phone className="w-8 h-8 text-amber" />
              <h2 className="mt-5 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white group-hover:text-amber transition-colors">
                Call the command centre
              </h2>
              <p className="mt-3 font-mono text-lg text-amber">{SITE.phone}</p>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">
                Manned 24 hours a day, 365 days a year. Urgent requirements can
                typically deploy within 24 hours — sometimes the same day.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber">
                Tap to call <ArrowRight className="w-3 h-3" />
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={SITE.emailHref}
              data-testid="contact-email-card"
              className="corner-card spotlight group block bg-card border border-white/10 p-8 hover:border-amber/60 transition-colors duration-300 h-full"
            >
              <Mail className="w-8 h-8 text-amber" />
              <h2 className="mt-5 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white group-hover:text-amber transition-colors">
                Email the team
              </h2>
              <p className="mt-3 font-mono text-lg text-amber break-all">{SITE.email}</p>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">
                Send drawings, site details or tender documents — a written
                response with next steps comes back the same working day.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber">
                Tap to email <ArrowRight className="w-3 h-3" />
              </span>
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="border border-white/10 bg-card p-5 flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber shrink-0 mt-0.5" />
              <div>
                <p className="font-display font-bold uppercase tracking-wide text-white text-sm">Always open</p>
                <p className="mt-1 text-xs text-gray-500 leading-relaxed">24 hours a day, 365 days a year — nights, weekends and bank holidays included.</p>
              </div>
            </div>
            <div className="border border-white/10 bg-card p-5 flex items-start gap-3">
              <MapPin className="w-5 h-5 text-amber shrink-0 mt-0.5" />
              <div>
                <p className="font-display font-bold uppercase tracking-wide text-white text-sm">Coverage</p>
                <p className="mt-1 text-xs text-gray-500 leading-relaxed">120+ towns and cities across all nine regions of England.</p>
              </div>
            </div>
            <div className="border border-white/10 bg-card p-5 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-amber shrink-0 mt-0.5" />
              <div>
                <p className="font-display font-bold uppercase tracking-wide text-white text-sm">Credentials</p>
                <p className="mt-1 text-xs text-gray-500 leading-relaxed">SIA licensed, BS 7858 vetted, £10m insured — evidence available on request.</p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <a
            href={SITE.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-gbp-link"
            className="group mt-4 flex items-center justify-between gap-4 border border-white/10 bg-card p-6 hover:border-amber transition-colors duration-300"
          >
            <div className="flex items-start gap-4">
              <MapPin className="w-6 h-6 text-amber shrink-0" />
              <div>
                <p className="font-display font-bold uppercase tracking-wide text-white group-hover:text-amber transition-colors">
                  Headquarters — Sunderland, North East England
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  {SITE.address} · Find us and review us on Google
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-amber shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-20" data-testid="contact-process">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="01" label="WHAT HAPPENS NEXT" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight mb-10">
              From first call to first shift
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="corner-card bg-card border border-white/10 p-6 h-full hover:border-amber/60 transition-colors duration-300">
                  <div className="flex items-center justify-between">
                    <s.icon className="w-6 h-6 text-amber" />
                    <span className="font-mono text-[10px] text-amber tracking-widest">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display font-bold uppercase tracking-wide text-xl text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-gray-400 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 sm:py-20" data-testid="contact-coverage">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="02" label="WHERE WE OPERATE" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight mb-4">
              Every region of England
            </h2>
            <p className="max-w-2xl text-sm sm:text-base text-gray-400 leading-relaxed mb-10">
              Locally based officers, regional supervision and a national command
              centre. Find your town or city for local coverage detail — or just
              call, and we will confirm cover for your exact site in minutes.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="contact-regions">
            {REGION_ORDER.map((region, i) => (
              <Reveal key={region} delay={(i % 3) * 0.06}>
                <Link
                  to="/locations"
                  data-testid={`contact-region-${region.toLowerCase().replaceAll(" ", "-")}`}
                  className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-5 py-4 hover:border-amber transition-colors duration-300"
                >
                  <span>
                    <span className="block font-display font-bold uppercase tracking-wide text-base text-white group-hover:text-amber transition-colors">
                      {region}
                    </span>
                    <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                      {locationsByRegion(region).length} locations covered
                    </span>
                  </span>
                  <ArrowRight className="w-4 h-4 text-amber shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
